/**
 * Icon — thin wrapper over lucide-react-native with a kebab-name registry so
 * screens/data can reference icons by the names used in the design handoff
 * (e.g. `icon: 'palmtree'`). Unknown names fall back to a neutral circle.
 */
import {
  House, FileText, BookMarked, GraduationCap, Users, Network, Newspaper, Sparkles,
  Calendar, Settings, CircleHelp, ChevronLeft, ChevronRight, ChevronDown, Search,
  Moon, Sun, Bell, CircleCheckBig, Inbox, TreePalm, Plane,
  Gift, LogOut, User, AtSign, Clock, UserPlus, UserMinus, UserCheck, Wallet,
  Workflow, Camera, BookOpen,
  Circle, Phone, Mail, MessageSquare, Heart, MessageCircle, Plus, X, Check, ArrowRight,
  Briefcase, Pin, CircleCheck, ExternalLink, MapPin, Building2, CircleDot, LayoutGrid,
  List, GitFork, Image, Paperclip, Share2, ArrowLeft, Send,
  Pencil, PenLine, LifeBuoy, Globe, Lock, Award, Play, CornerDownLeft, Eye, EyeOff,
  Info, TriangleAlert, RefreshCw, Link, Printer, ArrowRightLeft, Thermometer, SlidersHorizontal, Ellipsis,
  type LucideIcon,
} from 'lucide-react-native'
import { useTheme } from 'tamagui'

const REGISTRY: Record<string, LucideIcon> = {
  house: House,
  'file-text': FileText,
  printer: Printer,
  'book-marked': BookMarked,
  'graduation-cap': GraduationCap,
  users: Users,
  network: Network,
  newspaper: Newspaper,
  sparkles: Sparkles,
  calendar: Calendar,
  settings: Settings,
  'circle-help': CircleHelp,
  'chevron-left': ChevronLeft,
  'chevron-right': ChevronRight,
  'chevron-down': ChevronDown,
  search: Search,
  moon: Moon,
  sun: Sun,
  bell: Bell,
  'circle-check-big': CircleCheckBig,
  inbox: Inbox,
  palmtree: TreePalm,
  plane: Plane,
  gift: Gift,
  'log-out': LogOut,
  user: User,
  'at-sign': AtSign,
  clock: Clock,
  'user-plus': UserPlus,
  'user-minus': UserMinus,
  'user-check': UserCheck,
  wallet: Wallet,
  workflow: Workflow,
  camera: Camera,
  'book-open': BookOpen,
  phone: Phone,
  mail: Mail,
  'refresh-cw': RefreshCw,
  'message-square': MessageSquare,
  'message-circle': MessageCircle,
  heart: Heart,
  plus: Plus,
  x: X,
  check: Check,
  'arrow-right': ArrowRight,
  'arrow-right-left': ArrowRightLeft,
  briefcase: Briefcase,
  pin: Pin,
  'check-circle': CircleCheck,
  'external-link': ExternalLink,
  link: Link,
  'map-pin': MapPin,
  'building-2': Building2,
  'circle-dot': CircleDot,
  'layout-grid': LayoutGrid,
  list: List,
  'git-fork': GitFork,
  image: Image,
  paperclip: Paperclip,
  'share-2': Share2,
  'arrow-left': ArrowLeft,
  send: Send,
  pencil: Pencil,
  'pen-line': PenLine,
  'life-buoy': LifeBuoy,
  globe: Globe,
  lock: Lock,
  award: Award,
  play: Play,
  'corner-down-left': CornerDownLeft,
  eye: Eye,
  'eye-off': EyeOff,
  info: Info,
  'triangle-alert': TriangleAlert,
  thermometer: Thermometer,
  'sliders-horizontal': SlidersHorizontal,
  ellipsis: Ellipsis,
}

export interface IconProps {
  name: string
  size?: number
  color?: string
  strokeWidth?: number
}

export function Icon({ name, size = 18, color = 'currentColor', strokeWidth = 2 }: IconProps) {
  const Cmp = REGISTRY[name] ?? Circle
  const theme = useTheme()
  // lucide takes a raw colour string, so resolve Tamagui tokens ($primary,
  // $muted, $tertiary, …) here. This makes icon colours follow theme + accent.
  let resolved = color
  if (color.startsWith('$')) {
    const tok = (theme as Record<string, { get?: () => string } | undefined>)[color.slice(1)]
    if (tok?.get) resolved = tok.get() as string
  }
  return <Cmp size={size} color={resolved} strokeWidth={strokeWidth} />
}
